import type { PitstopRoutes } from "core/entities/AppTypes";
import type { CentrixSchemas } from "core/entities/CentrixTypes";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";

type FeatureFlagsDto = CentrixSchemas["Fixhub.Settings.PitstopFeatureFlagsDto"];
type FeatureFlagKeys = keyof FeatureFlagsDto;

const featureFlagPathMapping: Partial<
  Record<FeatureFlagKeys, PitstopRoutes | PitstopRoutes[]>
> = {
  financialPageActive: "/[region]/financial",
  interactiveMapPageActive: "/[region]/interactive-map",
  shopManagementActive: [
    "/[region]/manage/shops",
    "/[region]/manage/shops/[id]",
    "/[region]/manage/shops/[id]/change-request",
    "/[region]/manage/shops/change-requests",
  ],
};

const pathFeatureFlagMapping: Record<PitstopRoutes, FeatureFlagKeys> =
  Object.entries(featureFlagPathMapping).reduce(
    (acc, [flagKey, path]) => {
      if (Array.isArray(path)) {
        path.forEach((p) => {
          acc[p] = flagKey as FeatureFlagKeys;
        });
      } else {
        acc[path] = flagKey as FeatureFlagKeys;
      }
      return acc;
    },
    {} as Record<PitstopRoutes, FeatureFlagKeys>
  );

export type FeatureFlags = Record<FeatureFlagKeys, boolean>;

export const defaultFeatureFlags: FeatureFlags = {
  financialPageActive: false,
  interactiveMapPageActive: false,
  interactiveMapWidgetActive: false,
  marketplaceActive: false,
  sociReviewsActive: false,
  shopManagementActive: false,
};

export function useFeatureFlags(): FeatureFlags {
  const brcyCode = useBrcyCode();
  const { data, isLoading, isError } = useCentrixApi({
    path: "/api/app/pitstop-settings/feature-flags",
    parameters: { query: { brcyCode: brcyCode } },
  });
  if (isLoading || isError || !data) {
    return defaultFeatureFlags;
  }
  return {
    financialPageActive: data.financialPageActive ?? false,
    interactiveMapPageActive: data.interactiveMapPageActive ?? false,
    interactiveMapWidgetActive: data.interactiveMapWidgetActive ?? false,
    marketplaceActive: data.marketplaceActive ?? false,
    sociReviewsActive: data.sociReviewsActive ?? false,
    shopManagementActive: data.shopManagementActive ?? false,
  };
}

export function useIsFeatureFlagEnabled(
  featureFlagKey: FeatureFlagKeys
): boolean {
  const featureFlags = useFeatureFlags();
  return featureFlags[featureFlagKey];
}

type FeatureFlagByRouteResult =
  | {
      isFeatureFlagRoute: true;
      isEnabled: boolean;
      featureFlagKey: FeatureFlagKeys;
    }
  | {
      isFeatureFlagRoute: false;
      isEnabled: boolean;
    };
export function useFeatureFlagByRoute(
  route: PitstopRoutes
): FeatureFlagByRouteResult {
  const featureFlags = useFeatureFlags();
  const featureFlagKey = pathFeatureFlagMapping[route];
  if (!featureFlagKey) {
    return { isFeatureFlagRoute: false, isEnabled: true };
  }
  return {
    isFeatureFlagRoute: true,
    isEnabled: featureFlags[featureFlagKey],
    featureFlagKey,
  };
}
